import type { Character } from '@dcc/rules/domain/character.ts';
import { computeSheet, type SheetView } from '../services/characterService.ts';

export const CRAWLER_LIBRARY_DOC = 'crawler-library';

export interface GmRosterPlayer {
  playerId: string;
  label: string;
  revoked: boolean;
  characters: GmRosterCharacter[];
}

export interface GmRosterCharacter {
  id: string;
  name: string;
  active: boolean;
  archived: boolean;
  view: SheetView;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function buildGmRoster(playersRaw: unknown, documentsRaw: unknown): GmRosterPlayer[] {
  const players = Array.isArray(playersRaw) ? playersRaw : [];
  const documents = Array.isArray(documentsRaw) ? documentsRaw : [];

  const library = new Map<string, { activeId: string | null; archived: Set<string> }>();
  for (const raw of documents) {
    const doc = record(raw);
    if (doc.kind !== 'custom' || doc.key !== CRAWLER_LIBRARY_DOC || typeof doc.playerId !== 'string') continue;
    const data = record(doc.data);
    const archived = new Set(Array.isArray(data.archivedIds) ? data.archivedIds.filter((id): id is string => typeof id === 'string') : []);
    const activeId = typeof data.activeCharacterId === 'string' && !archived.has(data.activeCharacterId) ? data.activeCharacterId : null;
    library.set(doc.playerId, { activeId, archived });
  }

  const byPlayer = new Map<string, GmRosterCharacter[]>();
  for (const raw of documents) {
    const doc = record(raw);
    if (doc.kind !== 'character' || typeof doc.playerId !== 'string' || typeof doc.key !== 'string') continue;
    const data = record(doc.data);
    if (typeof data.id !== 'string' || typeof data.name !== 'string') continue;
    try {
      const view = computeSheet(doc.data as Character);
      const state = library.get(doc.playerId) ?? { activeId: null, archived: new Set<string>() };
      const rows = byPlayer.get(doc.playerId) ?? [];
      rows.push({
        id: doc.key,
        name: view.character.name,
        active: state.activeId === doc.key,
        archived: state.archived.has(doc.key),
        view,
      });
      byPlayer.set(doc.playerId, rows);
    } catch {
      // An unreadable sheet should not make every other player's library disappear.
    }
  }

  const result: GmRosterPlayer[] = [];
  for (const raw of players) {
    const player = record(raw);
    if (typeof player.playerId !== 'string' || typeof player.label !== 'string') continue;
    const characters = byPlayer.get(player.playerId) ?? [];
    characters.sort((a, b) => Number(b.active) - Number(a.active) || Number(a.archived) - Number(b.archived) || a.name.localeCompare(b.name));
    result.push({
      playerId: player.playerId,
      label: player.label,
      revoked: typeof player.revokedAt === 'number',
      characters,
    });
  }
  result.sort((a, b) => Number(a.revoked) - Number(b.revoked) || a.label.localeCompare(b.label));
  return result;
}
